import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  browserSignInLinkEmailStore,
  type EmailLinkSignIn,
  identityPlatformEmailLink,
  identityPlatformPasswordReset,
  type PasswordReset,
  type SignInLinkEmailStore,
  sendResetEmailQuietly,
} from '../../api';
import { type BrowserNavigation, routeState, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';
import { SignInLinkSentScreen, useSignInLinkRequest } from '../sign-in-link';
import { useResend } from '../use-resend';
import { ForgotPasswordScreen } from './forgot-password-screen';
import { ResetEmailSentScreen } from './reset-email-sent-screen';

/** What sending a reset email logs and announces (useResend). */
export const FORGOT_PASSWORD_MESSAGES = {
  failedLog: 'Sending the password reset email failed',
  sent: 'Email sent. Check your inbox, and your spam folder.',
  failed: "We couldn't send the email. Try again in a few minutes.",
} as const;

const RESEND_OPTIONS = { messages: FORGOT_PASSWORD_MESSAGES };

export interface ForgotPasswordPageProps {
  /** Identity Platform's password reset. Defaults to the browser's, loading Firebase on first use. */
  passwordReset?: PasswordReset;
  /** "Email me a sign-in link". Defaults to Identity Platform's, loading Firebase on first use. */
  emailLink?: EmailLinkSignIn;
  /** Where this device remembers the address a sign-in link went to. Defaults to localStorage. */
  emailStore?: SignInLinkEmailStore;
  /** The window's history and location. */
  navigation?: BrowserNavigation;
}

/**
 * /forgot-password: requests a password reset email (sendPasswordResetEmail), prefilled from the address in history
 * state. Every well-formed request shows "Check your email"; only a rate-limited or offline request shows an error,
 * and a rate-limited one cools down for RESEND_COOLDOWN_SECONDS, as does every send. The email's link opens
 * Firebase's hosted reset page, whose Continue goes to /sign-in keeping return_to. "Email me a sign-in link" sends a
 * sign-in link to the address instead (continuing to /sign-in/link, keeping return_to), with its own cooldown, and
 * shows "Check your email" worded for it.
 */
export const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
  passwordReset = identityPlatformPasswordReset,
  emailLink = identityPlatformEmailLink,
  emailStore = browserSignInLinkEmailStore,
  navigation,
}) => {
  const { switchTo, continueUrlTo } = useAuthNavigation(navigation);
  const { state } = useLocation();
  const [arrival] = useState(() => routeState(state));
  const [sentTo, setSentTo] = useState<string>();
  const [resent, setResent] = useState(false);
  // The address to send to, read by the send callback on the same tick as the submit that sets it.
  const address = useRef(arrival.email ?? '');
  const continueUrl = continueUrlTo(ROUTES.signIn);

  const resend = useResend(
    useCallback(() => sendResetEmailQuietly(passwordReset, address.current, continueUrl), [passwordReset, continueUrl]),
    RESEND_OPTIONS,
  );

  const signInLink = useSignInLinkRequest({ emailLink, emailStore, continueUrl: continueUrlTo(ROUTES.signInLink) });
  // Which request the form's alert is about: the one made last.
  const [lastRequest, setLastRequest] = useState<'reset' | 'link'>('reset');

  const sendSignInLink = (email: string) => {
    setLastRequest('link');
    void signInLink.request(email);
  };

  const submit = async (email: string) => {
    setLastRequest('reset');
    address.current = email;
    if (await resend.resend()) {
      setSentTo(email);
    }
  };

  const sendAgain = async () => {
    setResent(true);
    return resend.resend();
  };

  const toSignIn = (email: string | undefined) => switchTo(ROUTES.signIn, email ? { email } : undefined);

  return signInLink.sentTo ? (
    <SignInLinkSentScreen
      email={signInLink.sentTo}
      {...signInLink.sentScreen}
      onBack={() => toSignIn(signInLink.sentTo)}
    />
  ) : sentTo ? (
    <ResetEmailSentScreen
      email={sentTo}
      resend={{ ...resend, resend: sendAgain }}
      showStatus={resent}
      onBack={() => toSignIn(sentTo)}
    />
  ) : (
    <ForgotPasswordScreen
      defaultEmail={arrival.email}
      sending={resend.sending}
      cooldown={resend.cooldown}
      error={lastRequest === 'link' ? signInLink.resend.error : resend.error}
      onSubmit={(email) => void submit(email)}
      signInLink={signInLink.resend}
      onSendSignInLink={sendSignInLink}
      onBack={toSignIn}
    />
  );
};
